import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Prefetch essential data on app initialization to speed up navigation.
 * This hook runs once when the app mounts and preloads commonly used data.
 */
export const usePrefetch = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Prefetch all essential data in parallel
    const prefetchEssentialData = async () => {
      // 1. Prefetch public settings (used in Header, Footer, CTAs)
      queryClient.prefetchQuery({
        queryKey: ["settings-public"],
        queryFn: async () => {
          const { data, error } = await supabase.rpc("get_public_settings");
          if (error) throw error;
          const settings = Array.isArray(data) ? data[0] : data;
          return settings;
        },
        staleTime: 5 * 60 * 1000,
      });

      // 2. Prefetch social media links (used in Header, Footer)
      queryClient.prefetchQuery({
        queryKey: ["social-media"],
        queryFn: async () => {
          const { data, error } = await supabase
            .from("social_media")
            .select("*")
            .eq("is_active", true)
            .order("sort_order");
          if (error) throw error;
          return data;
        },
        staleTime: 5 * 60 * 1000,
      });

      // 3. Prefetch services hierarchy (used in Header navigation)
      queryClient.prefetchQuery({
        queryKey: ["services-hierarchy"],
        queryFn: async () => {
          const { data, error } = await supabase
            .from("services")
            .select("id, title, slug, parent_id, icon, sort_order, short_description")
            .eq("is_active", true)
            .order("sort_order", { ascending: true });

          if (error) throw error;

          const mainServices = data?.filter((s) => !s.parent_id) || [];
          const hierarchy = mainServices.map((main) => ({
            ...main,
            subServices: data?.filter((s) => s.parent_id === main.id) || [],
          }));

          return hierarchy;
        },
        staleTime: 5 * 60 * 1000,
      });

      // 4. Prefetch main services list (used on homepage, services page)
      queryClient.prefetchQuery({
        queryKey: ["main-services"],
        queryFn: async () => {
          const { data, error } = await supabase
            .from("services")
            .select("*")
            .eq("is_active", true)
            .is("parent_id", null)
            .order("sort_order", { ascending: true });
          if (error) throw error;
          return data;
        },
        staleTime: 5 * 60 * 1000,
      });

      // 5. Prefetch coverage areas (used on homepage, coverage page)
      queryClient.prefetchQuery({
        queryKey: ["coverage-areas"],
        queryFn: async () => {
          const { data, error } = await supabase
            .from("coverage_areas")
            .select("*")
            .eq("is_active", true)
            .order("sort_order", { ascending: true });
          if (error) throw error;
          return data;
        },
        staleTime: 10 * 60 * 1000,
      });

      // 6. Prefetch popular tags (used in blog sidebar, tag pages)
      queryClient.prefetchQuery({
        queryKey: ["popular-tags"],
        queryFn: async () => {
          const { data, error } = await supabase
            .from("tags")
            .select("*")
            .order("usage_count", { ascending: false })
            .limit(15);
          if (error) throw error;
          return data;
        },
        staleTime: 5 * 60 * 1000,
      });

      // 7. Prefetch blog categories (used in blog page)
      queryClient.prefetchQuery({
        queryKey: ["blog-categories"],
        queryFn: async () => {
          const { data, error } = await supabase
            .from("blog_categories")
            .select("*")
            .order("name");
          if (error) throw error;
          return data;
        },
        staleTime: 5 * 60 * 1000,
      });
    };

    // Start prefetching after a small delay to not block initial render
    const timeoutId = setTimeout(prefetchEssentialData, 100);

    return () => clearTimeout(timeoutId);
  }, [queryClient]);
};

export default usePrefetch;
